/**
 * Site-group creation form fields.
 *
 * The presentational inputs a customer fills in to stand up a new site group
 * (shown as "Plant" to billboard customers): its name, a description, and the
 * restricted virtual / digital-NOC / inventory options. Rendered inside a Formik context
 * owned by the enclosing dialog, which owns submission and error surfacing;
 * this component only draws the fields.
 *
 * Contains:
 * - SiteGroupForm — the field group
 */
import { Checkbox, FormControlLabel, FormGroup, FormHelperText, Stack, TextField } from '@mui/material';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

function SiteGroupForm({
  values,
  setFieldValue,
  isStaff = false,
  isBillboard = false,
  alreadyHasDigitalNoc = false,
  canViewInventoryGroups = false,
}) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2}>
      <Field name="name">
        {({ field, meta }) => (
          <TextField
            {...field}
            fullWidth
            label={t('general.name')}
            error={meta.touched && Boolean(meta.error)}
            helperText={meta.touched && meta.error ? meta.error : ''}
          />
        )}
      </Field>

      <Field name="description">
        {({ field, meta }) => (
          <TextField
            {...field}
            fullWidth
            label={t('general.description')}
            error={meta.touched && Boolean(meta.error)}
            helperText={meta.touched && meta.error ? meta.error : ''}
          />
        )}
      </Field>

      {isStaff && (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                name="is_virtual"
                checked={values.is_virtual}
                onChange={e => setFieldValue('is_virtual', e.target.checked)}
              />
            }
            label={t('siteGroups.isVirtual')}
          />
          <FormHelperText sx={{ ml: 2 }}>
            {t('siteGroups.isVirtualDescription', {
              sitesTerm: t('sites.sites'),
              siteTerm: t('sites.site'),
            })}
          </FormHelperText>
        </FormGroup>
      )}

      {canViewInventoryGroups && (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                name="is_inventory"
                checked={values.is_inventory || false}
                onChange={e => setFieldValue('is_inventory', e.target.checked)}
              />
            }
            label={t('siteGroups.isInventory')}
          />
          <FormHelperText sx={{ ml: 2 }}>
            {t('siteGroups.isInventoryDescription', {
              sitesTerm: t('sites.sites'),
              siteTerm: t('sites.site'),
            })}
          </FormHelperText>
        </FormGroup>
      )}

      {values.is_virtual && isBillboard && !alreadyHasDigitalNoc && (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                name="is_digital_noc"
                checked={values.is_digital_noc || false}
                onChange={e => {
                  setFieldValue('is_digital_noc', e.target.checked);
                  setFieldValue('is_virtual', e.target.checked);
                }}
              />
            }
            label={t('siteGroups.isDigitalNoc')}
          />
          <FormHelperText sx={{ ml: 2 }}>
            {t('siteGroups.isDigitalNocDescription', {
              sitesTerm: t('sites.sites'),
              siteTerm: t('sites.site'),
            })}
          </FormHelperText>
        </FormGroup>
      )}
    </Stack>
  );
}

SiteGroupForm.propTypes = {
  values: PropTypes.shape({
    is_virtual: PropTypes.bool,
    is_digital_noc: PropTypes.bool,
    is_inventory: PropTypes.bool,
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  isStaff: PropTypes.bool,
  isBillboard: PropTypes.bool,
  alreadyHasDigitalNoc: PropTypes.bool,
  canViewInventoryGroups: PropTypes.bool,
};

export default SiteGroupForm;
